import type { StudentDTO } from "@/modules/students/domain/contracts";
import { formatDate, getAgeYears } from "@/shared/lib/date-time";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Checkbox,
  DataTable,
  Label,
  confirm,
  createSortableColumn,
  type TanstackReactTable,
} from "@devhop/ui";
import { Link } from "@tanstack/react-router";
import {
  BanIcon,
  CheckIcon,
  EditIcon,
  EyeIcon,
  TrashIcon,
  XIcon,
} from "lucide-react";
import { useEffect, useState } from "react";

type StudentRow = StudentDTO;

export interface StudentsTableProps {
  data: StudentRow[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange: (id: string, desc: boolean) => void;
  onViewProfile: (student: StudentRow) => void;
  onEdit: (student: StudentRow) => void;
  onToggleStatus: (student: StudentRow) => void;
  onDelete: (id: string) => void;
  onBulkDelete?: (ids: string[]) => void;
  onBulkToggleStatus?: (
    ids: string[],
    status: "active" | "deactivated",
  ) => void;
}

export function StudentsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewProfile,
  onEdit,
  onToggleStatus,
  onDelete,
  onBulkDelete,
  onBulkToggleStatus,
}: StudentsTableProps) {
  const [columnSelection, setColumnSelection] = useState<string[]>([]);

  // Clear selection when data changes (pagination, filter, etc.)
  useEffect(() => {
    setColumnSelection([]);
  }, [data, offset]);

  const selectedCount = columnSelection.length;
  const allSelected = data.length > 0 && selectedCount === data.length;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setColumnSelection(data.map((row) => row.id));
    } else {
      setColumnSelection([]);
    }
  };

  const handleBulkDelete = async () => {
    if (!onBulkDelete || selectedCount === 0) return;

    const ok = await confirm({
      title: "ລຶບນັກຮຽນຫຼາຍຄົນ",
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບນັກຮຽນ ${selectedCount} ຄົນ?`,
      actionText: "ລຶບ",
      ActionProps: {
        variant: "destructive",
      },
    });

    if (ok) {
      onBulkDelete(columnSelection);
      setColumnSelection([]);
    }
  };

  const handleBulkToggleStatus = async (status: "active" | "deactivated") => {
    if (!onBulkToggleStatus || selectedCount === 0) return;

    const statusLabel = status === "active" ? "ເປີດການໃຊ້ງານ" : "ປິດການໃຊ້ງານ";
    const ok = await confirm({
      title: `ປ່ຽນສະຖານະນັກຮຽນ ${selectedCount} ຄົນ`,
      description: `ທ່ານແນ່ໃຈບໍ່ວ່າຈະ${statusLabel}ນັກຮຽນ ${selectedCount} ຄົນ?`,
      actionText: statusLabel,
    });

    if (ok) {
      onBulkToggleStatus(columnSelection, status);
      setColumnSelection([]);
    }
  };

  const columns: TanstackReactTable.ColumnDef<StudentRow>[] = [
    {
      enableSorting: false,
      accessorKey: "id",
      header: () => (
        <Checkbox
          checked={allSelected}
          onCheckedChange={handleSelectAll}
          aria-label="Select all"
          className="hidden md:block"
        />
      ),
      size: 50,
      cell: ({ row }) => (
        <Label className="flex items-center gap-2">
          <Checkbox
            checked={columnSelection.includes(row.original.id)}
            onCheckedChange={(checked) => {
              if (checked) {
                setColumnSelection([...columnSelection, row.original.id]);
              } else {
                setColumnSelection(
                  columnSelection.filter((id) => id !== row.original.id),
                );
              }
            }}
            aria-label={`Select ${row.original.name}`}
          />
          <span className="hidden font-medium text-sm max-md:block">ເລືອກ</span>
        </Label>
      ),
    },
    {
      id: "avatar",
      header: "ຮູບ",
      cell: ({ row }: { row: { original: StudentRow } }) => (
        <Avatar className="h-8 w-8">
          <AvatarImage
            src={resolveImageSrc(row.original.image) ?? undefined}
            alt={row.original.name}
          />
          <AvatarFallback>
            {getInitials(row.original.name)}
          </AvatarFallback>
        </Avatar>
      ),
      size: 50,
    },
    createSortableColumn("memberId", "Member ID"),
    createSortableColumn("nickname", "ຊື່ຫຼີ້ນ", {
      cell: ({ row }) => row.original.nickname || "-",
    }),
    createSortableColumn("name", "ຊື່-ນາມສະກຸນ", {
      cell: ({ row }) => {
        return (
          <Link
            to="/app/students/$id"
            params={{ id: row.original.id }}
            className="flex items-center gap-2 text-primary hover:underline"
          >
            <span>{row.original.name}</span>
          </Link>
        );
      },
    }),
    {
      header: "ກຸ່ມ",
      accessorKey: "groupName",
      cell: ({ row }) => row.original.groupName || "-",
    },
    {
      header: "ວັນເດືອນປີເກີດ",
      accessorKey: "dateOfBirth",
      cell: ({ row }) => {
        const dob = row.original.dateOfBirth;
        if (!dob) return "-";
        const age = getAgeYears(dob);
        return (
          <div>
            <div>{formatDate(dob)}</div>
            {age != null ? (
              <div className="text-muted-foreground text-xs">{age} ປີ</div>
            ) : null}
          </div>
        );
      },
    },
    {
      header: "ເພດ",
      accessorKey: "gender",
      cell: ({ row }) => {
        const g = row.original.gender;
        if (g == null) return "-";
        return g === "male" ? "ຊາຍ" : g === "female" ? "ຍິງ" : "ອື່ນໆ";
      },
    },
    {
      header: "ເບີໂທ",
      accessorKey: "phoneNumber",
      cell: ({ row }) => row.original.phoneNumber || "-",
    },
    {
      header: "ອີເມວ",
      accessorKey: "email",
      cell: ({ row }) => row.original.email || "-",
    },
    {
      header: "ສະຖານະ",
      accessorKey: "status",
      cell: ({ row }) => {
        const status = row.original.status;
        return (
          <Badge variant={status === "active" ? "default" : "secondary"}>
            {status === "active" ? "ໃຊ້ງານ" : "ປິດການໃຊ້ງານ"}
          </Badge>
        );
      },
    },
    {
      header: "ວັນທີສ້າງ",
      accessorKey: "createdAt",
      cell: ({ row }) => formatDate(row.original.createdAt),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const student = row.original;
        return (
          <RowActions
            actions={[
              {
                label: "ເບິ່ງໂປຣໄຟລ໌",
                icon: <EyeIcon className="size-4" />,
                onClick: () => onViewProfile(student),
              },
              {
                label: "ແກ້ໄຂ",
                icon: <EditIcon className="size-4" />,
                onClick: () => onEdit(student),
              },
              {
                label:
                  student.status === "active" ? "ປິດການໃຊ້ງານ" : "ເປີດການໃຊ້ງານ",
                icon:
                  student.status === "active" ? (
                    <BanIcon className="size-4" />
                  ) : (
                    <CheckIcon className="size-4" />
                  ),
                onClick: () => onToggleStatus(student),
              },
              {
                label: "ລຶບ",
                icon: <TrashIcon className="size-4" />,
                variant: "destructive",
                onClick: async () => {
                  const ok = await confirm({
                    title: "ລຶບນັກຮຽນ",
                    description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບນັກຮຽນນີ້?",
                    actionText: "ລຶບ",
                    ActionProps: {
                      variant: "destructive",
                    },
                  });
                  if (ok) {
                    onDelete(student.id);
                  }
                },
              },
            ]}
          />
        );
      },
    },
  ];

  return (
    <>
      {selectedCount > 0 && (
        <div className="flex flex-col gap-2 border-b bg-muted/50 px-4 py-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">
              ເລືອກ {selectedCount} ລາຍການ
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setColumnSelection([])}
            >
              <XIcon className="size-4" />
              <span className="hidden sm:inline">ຍົກເລີກ</span>
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {onBulkToggleStatus && (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("active")}
                  className="flex-1 sm:flex-initial"
                >
                  <CheckIcon className="size-4" />
                  <span className="hidden sm:inline">ເປີດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ເປີດ</span>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleBulkToggleStatus("deactivated")}
                  className="flex-1 sm:flex-initial"
                >
                  <BanIcon className="size-4" />
                  <span className="hidden sm:inline">ປິດການໃຊ້ງານ</span>
                  <span className="sm:hidden">ປິດ</span>
                </Button>
              </>
            )}
            {onBulkDelete && (
              <Button
                variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                className="flex-1 sm:flex-initial"
              >
                <TrashIcon className="size-4" />
                <span className="hidden sm:inline">ລຶບ ({selectedCount})</span>
                <span className="sm:hidden">ລຶບ</span>
              </Button>
            )}
          </div>
        </div>
      )}
      <DataTable<StudentRow, unknown>
        noDataMessage="ບໍ່ພົບນັກຮຽນ"
        isLoading={isLoading}
        columns={columns}
        data={data}
        offset={offset}
        limit={limit}
        totalCount={totalCount}
        onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortingChange={(sorting) => {
          if (sorting[0]?.id === "") return;
          onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
        }}
      />
    </>
  );
}
